// R33 Bindungspaket (Lead-Auftrag Phase 4, Fabi, 15.10.2026; docs/bindung.md Nr. 1, 2, 4, 12):
// Vorstellung neuer Freunde (F40), Erinnerungsspur (F39), Abschied als eigener Moment mit Wimpel (F38).
// Alles hier wählt Texte ohne den Zufallsgenerator des Runs, damit Kämpfe und Begegnungen gleich bleiben.
import { bondTexts, fromWhom, getSpecies, type MemoryKind } from '../../content/index.ts';
import { displayName } from './profile.ts';
import type { FarewellMoment, RunFriend, RunState } from './types.ts';

/** Fester Index aus einem Schlüssel: gleiche Eingabe, gleicher Text, ohne den Zufall des Runs zu berühren */
function stableIndex(key: string, n: number): number {
  let h = 2166136261;
  for (let i = 0; i < key.length; i++) h = Math.imul(h ^ key.charCodeAt(i), 16777619);
  return n ? (h >>> 0) % n : 0;
}
const stablePick = <T>(list: T[], key: string): T | undefined => list[stableIndex(key, list.length)];
const fillIn = (t: string, vars: Record<string, string>) => t.replace(/\{(\w+)\}/g, (m, k: string) => vars[k] ?? m);

/** F40: Vorlage der Vorstellung, beim Anlegen des Freundes festgelegt */
export function pickIntro(run: Pick<RunState, 'seed'>, f: Pick<RunFriend, 'id' | 'species'>): string | undefined {
  return stablePick(bondTexts.species[f.species]?.intro ?? [], `${run.seed}:${f.id}:intro`);
}
/** F40: Die Vorstellung mit dem heutigen Namen */
export function introText(f: Pick<RunFriend, 'intro' | 'name'>): string | null {
  return f.intro ? fillIn(f.intro, { name: f.name }) : null;
}

/** F39: Wie viel ein Moment für die Erinnerungsspur zählt [ZAHL?] */
const WEIGHT: Record<MemoryKind, number> = { start: 1, errand: 2, bewerbung: 2, ereignis: 2, auftrag: 2, boss: 3, befreit: 3, title: 4, promise: 4, lastStanding: 4 };

/** F39: einen besonderen Moment festhalten */
export function remember(run: Pick<RunState, 'day'>, f: RunFriend, kind: MemoryKind, vars: Record<string, string> = {}): void {
  const text = fillIn(bondTexts.memories[kind], vars);
  const list = (f.memories ??= []);
  if (list.some((m) => m.kind === kind && m.text === text && m.day === run.day)) return;
  list.push({ day: run.day, kind, text });
}

/** F39: höchstens vier Höhepunkte, die wichtigsten, bei Gleichstand die neueren; angezeigt nach Tagen */
export function topMemories(f: Pick<RunFriend, 'memories'>, n = 4): { day: number; text: string; kind: string }[] {
  const list = (f.memories ?? []).map((m, i) => ({ ...m, i }));
  return list
    .sort((a, b) => WEIGHT[b.kind] - WEIGHT[a.kind] || b.i - a.i)
    .slice(0, n)
    .sort((a, b) => a.day - b.day || a.i - b.i)
    .map((m) => ({ day: m.day, text: m.text, kind: m.kind }));
}

/** F38: Wer reagiert auf einen Abschied: lieber jemand, der gerade mit am Ufer war */
function reactor(run: RunState, gone: RunFriend, party: string[]): RunFriend | undefined {
  const crew = run.crew.filter((f) => f.id !== gone.id);
  return crew.find((f) => party.includes(f.id)) ?? crew[0];
}

/** F38: Der Abschied bekommt seinen eigenen Moment: letzte Worte und eine Reaktion an Bord */
export function farewellMoment(run: RunState, gone: RunFriend, party: string[]): void {
  const mate = reactor(run, gone, party);
  const words = (bondTexts.species[gone.species]?.lastWords ?? []).filter((w) => mate || !w.includes('{freund}'));
  const vars = { name: gone.name, freund: mate?.name ?? '' };
  const moment: FarewellMoment = {
    friendId: gone.id,
    name: gone.name,
    species: gone.species,
    words: fillIn(stablePick(words, `${run.seed}:${gone.id}:words`) ?? '', vars),
    reaction: mate ? fillIn(stablePick(bondTexts.reactions, `${run.seed}:${gone.id}:reaction`) ?? '', vars) : null,
    ...wistful(run, gone),
  };
  (run.farewells ??= []).push(moment);
}

/**
 * F43 (Fabi, Phase Kern: „Wie weh ein Abschied tut, finde ich toll“): Der Abschied erinnert daran, wie lange er dabei war,
 * wie alles anfing (beim Bewerber mit seinem Brief) und woher die Wunde kam, die nicht mehr heilen konnte.
 */
function wistful(run: RunState, f: RunFriend): Pick<FarewellMoment, 'together' | 'beginning' | 'wound'> {
  const days = run.day - f.origin.day + 1;
  const shores = f.tally.landings;
  const span = days <= 1 ? 'nur diesen einen Tag' : `${days} Tage lang`;
  const together = `${f.name} war ${span} mit euch auf dem Floß und an ${shores === 1 ? 'einem Ufer' : `${shores} Ufern`} dabei.`;
  const start = (f.memories ?? []).find((m) => m.kind === 'start' || m.kind === 'bewerbung' || m.kind === 'befreit' || m.kind === 'ereignis' || m.kind === 'auftrag');
  const firstLine = f.letter ? f.letter.split('\n')[1]?.split(/(?<=[.!?])\s/)[0] : undefined;
  const beginning = firstLine
    ? `Wisst ihr noch, wie der Brief anfing? „${firstLine.charAt(0).toUpperCase()}${firstLine.slice(1)}“`
    : start
      ? `Am ${start.day}. Tag fing alles an: ${f.name} ${start.text}.`
      : undefined;
  const w = f.wound;
  const wound = w ? `Die Wunde ${w.by ? `${fromWhom(w.by)} ` : ''}vom ${w.day}. Tag war noch nicht verheilt.` : undefined;
  return { together, ...(beginning ? { beginning } : {}), ...(wound ? { wound } : {}) };
}

/** F38: Der Spieler hängt selbst den Wimpel auf; danach flattert er am Floß */
export function takePennant(run: RunState, friendId: string): string | null {
  const i = (run.farewells ?? []).findIndex((m) => m.friendId === friendId);
  if (i < 0) return null;
  run.farewells!.splice(i, 1);
  if (!(run.pennants ??= []).includes(friendId)) run.pennants.push(friendId);
  const f = run.gone.find((x) => x.id === friendId);
  return f ? `Am Floß hängt jetzt ein Wimpel für ${displayName(f)}.` : null;
}

/** F38: Was man beim Wimpel liest */
export function pennantLine(f: RunFriend): string {
  const sp = getSpecies(f.species);
  const best = topMemories(f, 1)[0];
  const day = f.gone?.day ?? 0;
  const shores = f.tally.landings;
  return `${f.name} ${sp.article} ${sp.name} war an ${shores === 1 ? 'einem Ufer' : `${shores} Ufern`} mit euch und ist am ${day}. Tag geblieben.${best ? ` ${f.name} ${best.text}.` : ''}`;
}
